<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import campanhasReportAPI from 'dashboard/api/campanhasReport';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAcesso } from 'dashboard/composables/useAcesso';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import ConversationLink from '../analytics/ConversationLink.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { campanhasLabel } from './campanhasLabels';

// Quem pediu para parar, de 25 em 25: cada pedido é uma linha, com quando e
// como. O pedido bloqueia o próximo disparo, e vale para todo disparo,
// inclusive utilidade (G7). Só o administrador desfaz — o pedido deixa de
// bloquear todo disparo —, e só depois de confirmar: a tela não refaz o pedido.
// O desfeito continua na lista, com a data em que foi desfeito. Sem telefone: o
// contato aparece pelo nome, e a conversa abre.
const props = defineProps({
  // O recorte da API (`campanhasApiFilters`).
  filters: { type: Object, required: true },
  timezone: { type: String, default: undefined },
});

const { t } = useI18n();
const { number, dateTime } = useAnalyticsFormat();
const { isAdmin } = useAdmin();
// ACESSO-01 (L8b): desfazer o pedido de quem não quer receber mensagens (`contact_opt_outs#destroy`): com a chave
// ACESSO_GRANULAR ligada, o `contacts:manage`; desligada, o administrador, como hoje.
const { decide } = useAcesso();
const desfazDescadastro = computed(() =>
  decide('contacts:manage', () => isAdmin.value)
);

const page = ref(1);
const result = ref(null);
const loading = ref(false);
const failed = ref(false);
// O id do pedido que falhou ao desfazer: o motivo aparece na linha dele.
const undoFailed = ref(null);
const undoing = ref(null);
let lastRequest = 0;

const load = async () => {
  lastRequest += 1;
  const request = lastRequest;
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await campanhasReportAPI.descadastros(
      props.filters,
      page.value
    );
    if (request !== lastRequest) return;
    result.value = data;
  } catch {
    if (request !== lastRequest) return;
    result.value = null;
    failed.value = true;
  } finally {
    if (request === lastRequest) loading.value = false;
  }
};

watch(
  () => JSON.stringify(props.filters),
  () => {
    page.value = 1;
    load();
  },
  { immediate: true }
);

const goTo = next => {
  page.value = next;
  load();
};

// Desfeito, a lista é pedida de novo: a data da revogação é a do servidor.
const undo = async item => {
  undoing.value = item.id;
  undoFailed.value = null;
  try {
    await campanhasReportAPI.desfazerDescadastro(item.id);
    await load();
  } catch {
    undoFailed.value = item.id;
  } finally {
    undoing.value = null;
  }
};

// Um clique errado voltaria a liberar o disparo para quem pediu para parar, e a
// tela não tem como refazer o pedido: o "desfazer" pergunta antes, com a frase
// da G7.
const confirmRef = ref(null);
const pending = ref(null);

const askUndo = item => {
  pending.value = item;
  confirmRef.value?.open();
};

const confirmUndo = () => {
  const item = pending.value;
  confirmRef.value?.close();
  if (item) undo(item);
};

const items = computed(() => result.value?.items || []);
const total = computed(() => result.value?.total || 0);
const pages = computed(() =>
  Math.max(Math.ceil(total.value / (result.value?.per_page || 25)), 1)
);
const alreadyOptedOut = computed(() => result.value?.already_opted_out || 0);

const detail = item =>
  [
    dateTime(item.at, props.timezone),
    campanhasLabel(t, 'origin', item.origin),
  ].join(' · ');
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanha-descadastros"
    :title="t('CAMPANHAS.OPT_OUTS.TITLE')"
    :description="t('CAMPANHAS.OPT_OUTS.ALL_SENDS')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <template v-else-if="result">
      <p
        v-if="result.blocks_next"
        data-test-id="campanha-descadastros-bloqueia"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ t('CAMPANHAS.OPT_OUTS.BLOCKS_NEXT') }}
      </p>
      <p
        v-if="alreadyOptedOut"
        data-test-id="campanha-descadastros-ja-pedido"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{
          t(
            'CAMPANHAS.OPT_OUTS.ALREADY',
            { count: number(alreadyOptedOut) },
            alreadyOptedOut
          )
        }}
      </p>
      <p v-if="!items.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('CAMPANHAS.OPT_OUTS.EMPTY') }}
      </p>
      <ul
        v-else
        class="flex flex-col m-0 list-none divide-y divide-n-weak"
        :class="{ 'opacity-60': loading }"
      >
        <li
          v-for="item in items"
          :key="item.id"
          :data-test-id="`campanha-descadastro-${item.id}`"
          class="flex flex-wrap gap-x-3 gap-y-1 justify-between items-center py-2 min-w-0 text-sm"
        >
          <span class="flex flex-col min-w-0">
            <span class="truncate text-n-slate-12">
              {{ item.contact_name || t('CAMPANHAS.OPT_OUTS.NO_CONTACT') }}
            </span>
            <span class="text-xs text-n-slate-11">{{ detail(item) }}</span>
            <span
              v-if="item.revoked_at"
              :data-test-id="`campanha-descadastro-${item.id}-desfeito`"
              class="text-xs text-n-amber-11"
            >
              {{
                t('CAMPANHAS.OPT_OUTS.REVOKED_AT', {
                  time: dateTime(item.revoked_at, timezone),
                })
              }}
            </span>
            <span v-if="undoFailed === item.id" class="text-xs text-n-ruby-11">
              {{ t('CAMPANHAS.OPT_OUTS.UNDO_FAILED') }}
            </span>
          </span>
          <span class="flex gap-3 items-center">
            <ConversationLink
              v-if="item.display_id"
              :conversation-id="item.display_id"
            />
            <Button
              v-if="desfazDescadastro && !item.revoked_at"
              :data-test-id="`campanha-descadastro-${item.id}-desfazer`"
              :label="t('CAMPANHAS.OPT_OUTS.UNDO')"
              xs
              slate
              faded
              type="button"
              :is-loading="undoing === item.id"
              :disabled="Boolean(undoing)"
              @click="askUndo(item)"
            />
          </span>
        </li>
      </ul>
      <div
        v-if="pages > 1"
        class="flex gap-2 justify-between items-center text-xs text-n-slate-11"
      >
        <Button
          :label="t('CAMPANHAS.CONVERSATIONS.PREVIOUS')"
          xs
          slate
          faded
          type="button"
          :disabled="page <= 1 || loading"
          @click="goTo(page - 1)"
        />
        <span>
          {{ t('CAMPANHAS.TABLE.PAGE', { page, pages }) }}
        </span>
        <Button
          :label="t('CAMPANHAS.CONVERSATIONS.NEXT')"
          xs
          slate
          faded
          type="button"
          :disabled="page >= pages || loading"
          @click="goTo(page + 1)"
        />
      </div>
    </template>
    <Dialog
      v-if="desfazDescadastro"
      ref="confirmRef"
      type="alert"
      width="md"
      :title="t('CAMPANHAS.OPT_OUTS.UNDO_CONFIRM.TITLE')"
      :description="t('CAMPANHAS.OPT_OUTS.UNDO_CONFIRM.DESCRIPTION')"
      :confirm-button-label="t('CAMPANHAS.OPT_OUTS.UNDO_CONFIRM.CONFIRM')"
      @confirm="confirmUndo"
      @close="pending = null"
    >
      <p
        v-if="pending"
        data-test-id="campanha-descadastro-confirmar"
        class="mb-0 text-sm text-n-slate-12"
      >
        {{ pending.contact_name || t('CAMPANHAS.OPT_OUTS.NO_CONTACT') }}
        <span class="block text-xs text-n-slate-11">{{ detail(pending) }}</span>
      </p>
    </Dialog>
  </AnalyticsPanel>
</template>
